import { useEffect, useState } from 'react';
import { Send, Trash2, Pencil, AlertTriangle, Check, X } from 'lucide-react';
import { api } from '../lib/api';
import { AUDIENCE_LABEL, fmtWhen } from '../lib/shipments';
import { canSettle, SETTLE_AFTER_MIN } from '../lib/shipmentSettle.mjs';

// Draft → approve → send. Nothing goes out until a partner taps "Approve & send"
// (and confirms the recipient list). The server re-checks every recipient.
// `now` is the clock (milliseconds) the stuck-send marks are timed by; the pages leave it as is.
export default function ShipmentMessages({ messages, onChanged, emptyText = 'No updates waiting.', now = Date.now }) {
  if (!messages?.length) return <p className="text-sm text-[var(--text-secondary)]">{emptyText}</p>;
  return (
    <div className="space-y-3">
      {messages.map((m) => <MessageCard key={m.id} m={m} onChanged={onChanged} now={now} />)}
    </div>
  );
}

function MessageCard({ m, onChanged, now }) {
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState((m.to_emails || []).join(', '));
  const [subject, setSubject] = useState(m.subject);
  const [body, setBody] = useState(m.body_text);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);
  const editable = ['draft', 'failed'].includes(m.status) && !m.send_ambiguous;
  // Left 'sending': the provider took it but the record did not update (or it is going
  // out right now). The server refuses a resend, edit or discard, so the card offers none of those.
  const notRecorded = m.status === 'sending';
  // LP-1560: once the send started long enough ago that it is not going out any more, a partner
  // marks it sent or failed by hand from the Resend log (the server re-checks the age).
  const stuck = notRecorded && canSettle(m, now());
  // A card opened before then shows the marks when the time comes, without a reload.
  useEffect(() => {
    if (!notRecorded || stuck) return undefined;
    const due = Date.parse(m.updated_at) + SETTLE_AFTER_MIN * 60 * 1000 - now();
    if (!Number.isFinite(due)) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), Math.min(Math.max(0, due) + 1000, 2147483647));
    return () => clearTimeout(t);
  }, [notRecorded, stuck, m.updated_at, now, tick]);

  async function run(fn) {
    setBusy(true); setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(e.message);
    } finally {
      // A failed send can still change the message on the server.
      // Reload so a retry approves the revision the server holds now.
      try { await onChanged?.(); } catch { /* the page shows its own load error */ }
      setBusy(false);
    }
  }

  function openEditor() {
    // Start from the message as it is now, so an abandoned edit never comes back.
    setTo((m.to_emails || []).join(', '));
    setSubject(m.subject);
    setBody(m.body_text);
    setEditing(true);
  }

  const save = () => run(async () => {
    await api.patch(`/shipments/messages/${m.id}`, { to_emails: to, subject, body_text: body });
    setEditing(false);
  });

  const approveAndSend = () => run(async () => {
    const list = (m.to_emails || []).join(', ') || '(no recipients)';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send "${m.subject}" to:\n${list}\n\nThis emails them now from notifications@concria.us.`)) return;
    // Approve exactly the revision shown on screen (server 409s if it changed).
    if (m.status !== 'approved') await api.post(`/shipments/messages/${m.id}/approve`, { revision: m.updated_at });
    await api.post(`/shipments/messages/${m.id}/send`, {});
  });

  const discard = () => run(async () => { await api.post(`/shipments/messages/${m.id}/discard`, {}); });

  const settle = (outcome) => {
    const ask = outcome === 'sent'
      ? `Mark "${m.subject}" as sent?\n\nOnly if the Resend log shows it went out. Nothing is emailed.`
      : `Mark "${m.subject}" as failed?\n\nOnly if the Resend log does not show it. Nothing is emailed; you can then send it again unchanged or discard it.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(ask)) return;
    // The send shown on screen (server 409s if it changed).
    run(async () => { await api.post(`/shipments/messages/${m.id}/settle`, { outcome, revision: m.updated_at }); });
  };

  const statusChip = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-200',
    approved: 'bg-blue-100 text-blue-700', sending: 'bg-amber-100 text-amber-800',
    sent: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
  }[m.status] || 'bg-gray-100 text-gray-600';

  return (
    <div className="rounded-lg border border-[var(--border)] bg-white p-3 dark:bg-slate-900">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-[var(--text-primary)]">{AUDIENCE_LABEL[m.audience] || m.audience}</span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${statusChip}`}>{notRecorded ? 'sent, record not updated' : m.status}</span>
        <span className="text-[var(--text-secondary)]">{fmtWhen(m.sent_at || (notRecorded && m.updated_at) || m.created_at)}</span>
      </div>
      {m.internal_note && (
        <p className="mb-2 flex items-start gap-1.5 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span><b>Partners only:</b> {m.internal_note}</span>
        </p>
      )}
      {editing ? (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">To (comma separated; must be approved contacts)
            <input value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full rounded border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded border border-[var(--border)] bg-white px-2 py-1.5 text-sm font-medium dark:bg-slate-800" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} className="w-full rounded border border-[var(--border)] bg-white px-2 py-1.5 font-mono text-xs dark:bg-slate-800" />
          <div className="flex gap-2">
            <button disabled={busy} onClick={save} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"><Check size={13} className="mr-1 inline" />Save draft</button>
            <button disabled={busy} onClick={() => setEditing(false)} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-[var(--text-secondary)]">To: {(m.to_emails || []).join(', ') || <span className="font-semibold text-red-600">no approved recipients yet</span>}</p>
          <p className="mt-1 text-sm font-medium text-[var(--text-primary)]">{m.subject}</p>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap font-sans text-sm text-[var(--text-primary)]">{m.body_text}</pre>
          {m.send_error && <p className="mt-1 text-xs text-red-600">Last attempt failed: {m.send_error}</p>}
          {notRecorded && (
            <p className="mt-1 text-xs text-red-600">
              The email WAS sent (or is going out right now) but the record did not update. Do not resend. The Resend log shows whether it was delivered.
            </p>
          )}
          {notRecorded && !stuck && (
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              If this is still here {SETTLE_AFTER_MIN} minutes after the time above, check the Resend log and mark it sent or failed.
            </p>
          )}
          {m.status === 'sent' && m.settled_as === 'sent' && (
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Marked as sent by {m.settled_by || 'a partner'} on {fmtWhen(m.settled_at)}, from the Resend log (its record had not updated).
            </p>
          )}
        </>
      )}
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      {!editing && stuck && (
        <div className="mt-3">
          <p className="text-xs text-[var(--text-secondary)]">
            Check the Resend log first: find this email by the time above and its To list. If it is there, mark it sent. If it is not, mark it failed; you can then send it again unchanged or discard it. Neither button emails anyone.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => settle('sent')} className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
              <Check size={13} /> Mark as sent
            </button>
            <button disabled={busy} onClick={() => settle('failed')} className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
              <X size={13} /> Mark as failed
            </button>
          </div>
        </div>
      )}
      {!editing && (editable || m.status === 'approved' || (m.status === 'failed' && m.send_ambiguous)) && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button disabled={busy || !(m.to_emails || []).length} onClick={approveAndSend}
            className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-40">
            <Send size={13} /> {m.send_ambiguous ? 'Retry unchanged' : 'Approve & send'}
          </button>
          {editable && (
            <button disabled={busy} onClick={openEditor} className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
              <Pencil size={13} /> Edit
            </button>
          )}
          <button disabled={busy} onClick={discard} className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-red-600">
            <Trash2 size={13} /> Discard
          </button>
        </div>
      )}
    </div>
  );
}
